import { useMarkHasViewedResultsMutation } from '@/app/redux/endpoints/organisation';
import { useAppSelector } from '@/app/redux/hooks';
import { useAssessments } from '@/app/redux/hooks/assessments';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { selectShareLink } from '@/app/redux/slices/shareLinkSlice';
import { Assessment } from '@/common/types/assessment';
import { Activity, Attempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import getActivityContext from '@/common/utils/activityContext';
import { mapActivityToSkill } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import AssessmentAttemptTable from '@/dashboard/LearnerView/ActivityFeed/AttemptTables/AssessmentAttemptTable';
import MorphMasterAttemptTable from '@/dashboard/LearnerView/ActivityFeed/AttemptTables/MorphMasterAttemptTable';
import api from '@/db/apiClient';
import { HandGrabbingIcon, MouseLeftClickIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { capitalize } from 'lodash';
import { KeyboardIcon } from 'lucide-react';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import MysticMatchAttemptTable from './AttemptTables/MysticMatchAttemptTable';
import PhonemeForgeAttemptTable from './AttemptTables/PhonemeForgeAttemptTable';
import SentenceBuilderAttemptTable from './AttemptTables/SentenceBuilderAttemptTable';
import TrickyWordsAttemptTable from './AttemptTables/TrickyWordsAttemptTable';
import WordRiftsAttemptTable from './AttemptTables/WordRiftsAttemptTable';
import WordSumsAttemptTable from './AttemptTables/WordSumsAttemptTable';
import WordWeaverAttemptTable from './AttemptTables/WordWeaverAttemptTable';
import GenericResultDialogue from './GenericResultDialogue';
import exportToCSV from './attemptCSVExports';

interface ActivityDialogProps {
  studentId: string;
  activity: Activity | null;
  onShare: () => void;
  onClose: () => void;
}

export default function ActivityDialog({
  studentId,
  activity,
  onShare,
  onClose,
}: ActivityDialogProps) {
  const { organisation } = useOrganisation();
  const { studentsById } = useStudents();

  const userId = useAppSelector(selectUserId);
  const { assessmentsById } = useAssessments();
  const { shareLink, student: shareLinkStudent } =
    useAppSelector(selectShareLink);
  const student = shareLinkStudent ?? studentsById.get(studentId);
  const organisationId = shareLinkStudent
    ? shareLinkStudent.organisationId
    : organisation?.id;

  const [markResultsAsViewed] = useMarkHasViewedResultsMutation();

  const dialogOpenTime = useRef<number | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<Assessment>();
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [finalAttemptEndedAt, setFinalAttemptEndedAt] = useState<number | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      if (!activity || !activity.assessmentId) return;
      if (api.asShareLink) {
        const assessment = await api.asShareLink.getAssessment(
          activity.assessmentId,
        );
        setAssessment(assessment ?? undefined);
      } else {
        setAssessment(assessmentsById.get(activity.assessmentId));
      }
    })();
  }, [activity, assessmentsById, shareLink]);

  const placementLevel = useMemo(() => {
    if (!assessment || !activity?.endedAt) return;
    return assessment.students[studentId]?.placementLevel ?? undefined;
  }, [studentId, activity, assessment]);

  const activityDescription = useMemo(() => {
    if (!activity) return '';
    let activityDescription = mapActivityToSkill(activity.activityName).Skill;
    if (activity.version === 2) {
      return getActivityContext(activity.activityName, activity.level);
    } else if ('context' in activity) {
      return activity.context;
    }
    return activityDescription;
  }, [activity]);

  const applySentenceStyle = (sentence: string) => {
    return capitalize(sentence) + '.';
  };

  const correctAttempts = useMemo(() => {
    return attempts.reduce((acc, attempt) => {
      let correctAnswer = attempt.correctAnswer;
      const studentAnswer = attempt.studentAnswer;

      if (attempt.activityName === 'wordrifts') {
        correctAnswer = attempt.correctAnswer.toLowerCase();
      } else if (attempt.activityName === 'sentencebuilder') {
        correctAnswer = applySentenceStyle(attempt.correctAnswer);
      }

      return correctAnswer === studentAnswer ? acc + 1 : acc;
    }, 0);
  }, [attempts]);

  const controlTypeIcons: ReactNode | undefined = useMemo(() => {
    const withTooltip = (node: ReactNode, tip: string) => (
      <Tooltip key={tip}>
        <TooltipTrigger>{node}</TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    );
    if (activity && 'controlTypesUsed' in activity) {
      const icons = activity.controlTypesUsed
        .map((control) => {
          switch (control) {
            case ControllerType.CLICK:
              return withTooltip(
                <MouseLeftClickIcon size={16} />,
                'Click to Place',
              );
            case ControllerType.DRAG:
              return withTooltip(
                <HandGrabbingIcon size={16} />,
                'Drag and Drop',
              );
            case ControllerType.KEYBOARD:
              return withTooltip(<KeyboardIcon size={16} />, 'Typing');
            default:
              return undefined;
          }
        })
        .filter((i) => i !== undefined);
      return <div className="flex items-center">{icons}</div>;
    } else return undefined;
  }, [activity]);

  const handleExport = () => {
    if (!activity) return;

    const accuracy = attempts.length
      ? Math.round((correctAttempts / attempts.length) * 100)
      : 0;

    trackMixpanelEvent('Activity Result Exported', {
      studentId,
      activityId: activity.id,
      activityName: mapActivityToSkill(activity.activityName).CleanName,
      attemptCount: attempts.length,
      accuracy,
    });

    exportToCSV(activity, attempts);
  };

  // Track when dialog opens
  useEffect(() => {
    if (activity && !dialogOpenTime.current) {
      dialogOpenTime.current = Date.now();

      const accuracy = attempts.length
        ? Math.round((correctAttempts / attempts.length) * 100)
        : 0;

      trackMixpanelEvent('Activity Result Opened', {
        studentId,
        activityId: activity.id,
        activityName: mapActivityToSkill(activity.activityName).CleanName,
        activityType: activity.activityName,
        accuracy,
      });

      if (organisation && !organisation.activationChecklist.hasViewedResults) {
        markResultsAsViewed({ orgId: organisation.id });
      }
    }
  }, [
    activity,
    studentId,
    userId,
    attempts.length,
    correctAttempts,
    organisation,
    markResultsAsViewed,
  ]);

  useEffect(() => {
    (async () => {
      if (!activity?.id || !organisationId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      const service = api.asShareLink ?? api.asEducator;
      const attempts = await service.getAttemptsForActivity(
        organisationId,
        activity.id,
      );
      setAttempts(attempts);
      const finalAttempt = attempts.reduce(
        (final, attempt) =>
          !final || attempt.endedAt > final.endedAt ? attempt : final,
        null as Attempt | null,
      );
      if (finalAttempt) setFinalAttemptEndedAt(finalAttempt.endedAt);
      setLoading(false);
    })();
  }, [activity, organisationId, shareLink]);

  function renderAttemptsTable() {
    switch (activity?.activityName) {
      case 'mysticMatch':
        return <MysticMatchAttemptTable attempts={attempts} />;
      case 'wordWeaver':
        return <WordWeaverAttemptTable attempts={attempts} />;
      case 'phonemeForge':
        return <PhonemeForgeAttemptTable attempts={attempts} />;
      case 'sentenceBuilder':
        return <SentenceBuilderAttemptTable attempts={attempts} />;
      case 'trickyWords':
        return <TrickyWordsAttemptTable attempts={attempts} />;
      case 'wordRifts':
        return <WordRiftsAttemptTable attempts={attempts} />;
      case 'wordSums':
        return <WordSumsAttemptTable attempts={attempts} />;
      case 'assessment':
        return <AssessmentAttemptTable attempts={attempts} />;
      case 'morphMaster':
        return <MorphMasterAttemptTable attempts={attempts} />;
      default:
        return <div>Attempt details not available.</div>;
    }
  }

  const handleClose = () => {
    if (activity && dialogOpenTime.current) {
      const timeSpentMs = Date.now() - dialogOpenTime.current;

      trackMixpanelEvent('Activity Result Closed', {
        studentId,
        activityId: activity.id,
        timeSpentMs,
      });

      dialogOpenTime.current = null;
    }

    setAttempts([]);
    onClose();
  };

  if (!activity || !student) return null;

  return (
    <GenericResultDialogue
      open={!!activity}
      onClose={handleClose}
      title={mapActivityToSkill(activity.activityName).CleanName}
      studentName={`${student.studentFirstName} ${student.studentSurname}`}
      description={activityDescription}
      isLoading={isLoading}
      startedAt={activity.startedAt}
      endedAt={activity.endedAt ?? finalAttemptEndedAt}
      completed={!!activity.endedAt}
      controlTypeIcons={controlTypeIcons}
      correctAttempts={correctAttempts}
      totalAttempts={attempts.length}
      placementLevel={placementLevel}
      onExport={handleExport}
      onShare={onShare}
      renderAttemptsTable={renderAttemptsTable}
    />
  );
}
